import { QuestionNames } from '../question';
import { QuestionOption } from '../question/question.types';
import {
    UserSituationBooleanValue,
    UserSituationWhatIsYourCurrentPropertyGoalValue
} from '../../graphql.generated';

const propertyGoalsOptions = [
    {
        value: UserSituationWhatIsYourCurrentPropertyGoalValue.BuyAProperty,
        label: 'Buy a property'
    },
    {
        value: UserSituationWhatIsYourCurrentPropertyGoalValue.RentAProperty,
        label: 'Rent a property'
    },
    {
        value: UserSituationWhatIsYourCurrentPropertyGoalValue.SellMyProperty,
        label: 'Sell a property'
    },
    {
        value: UserSituationWhatIsYourCurrentPropertyGoalValue.TrackTheValueOfMyHome,
        label: 'Check the value of my home'
    },
    {
        value: UserSituationWhatIsYourCurrentPropertyGoalValue.Other,
        label: 'Other'
    }
];

const yesNoNotSayOptions = [
    { value: UserSituationBooleanValue.Yes, label: 'Yes' },
    { value: UserSituationBooleanValue.No, label: 'No' },
    {
        value: UserSituationBooleanValue.PreferNotToSay,
        label: "I'd prefer not to say"
    }
];

export type Step = {
    label: string;
    options: QuestionOption[];
    subtitle?: string;
    getNext: (value: string, situationBranch: string) => QuestionNames | 'done';
    formatAnswer: (answer: { value: string; label: string }) => string;
};

export const questionsMap: {
    [key in QuestionNames]: Step;
} = {
    situationWhatIsYourCurrentPropertyGoal: {
        label: 'What is your current property goal?',
        options: propertyGoalsOptions,
        getNext: (selectedValue) => {
            switch (selectedValue) {
                case UserSituationWhatIsYourCurrentPropertyGoalValue.BuyAProperty:
                    return 'situationAreYouAFirstTimeBuyer';
                case UserSituationWhatIsYourCurrentPropertyGoalValue.RentAProperty:
                    return 'done';
                case UserSituationWhatIsYourCurrentPropertyGoalValue.SellMyProperty:
                    return 'situationIsItAlreadyOnTheMarket';
                case UserSituationWhatIsYourCurrentPropertyGoalValue.TrackTheValueOfMyHome:
                    return 'done';
                case UserSituationWhatIsYourCurrentPropertyGoalValue.Other:
                    return 'situationAreYouAHomeowner';
                default:
                    return 'done';
            }
        },
        subtitle: 'I want to...',
        formatAnswer: ({ value, label }) => {
            if (value === 'other') {
                return 'Other';
            }

            return `I want to ${label.toLowerCase()}.`;
        }
    },
    situationAreYouAFirstTimeBuyer: {
        label: 'Are you a first-time buyer?',
        options: yesNoNotSayOptions,
        getNext: (selectedValue) => {
            switch (selectedValue) {
                case 'no':
                    return 'situationAreYouSellingAProperty';
                default:
                    return 'done';
            }
        },
        formatAnswer: ({ label }) => label
    },
    situationAreYouSellingAProperty: {
        label: 'Are you selling a property?',
        options: yesNoNotSayOptions,
        getNext: (selectedValue) => {
            switch (selectedValue) {
                case 'no':
                    return 'situationAreYouAPropertyInvestor';
                case 'yes':
                    return 'situationIsItAlreadyOnTheMarket';
                default:
                    return 'done';
            }
        },
        formatAnswer: ({ label }) => label
    },
    situationIsItAlreadyOnTheMarket: {
        label: 'Is it already on the market?',
        options: yesNoNotSayOptions,
        getNext: (selectedValue, situationAnswer) => {
            if (situationAnswer === 'buy_a_property') {
                return 'done';
            }

            switch (selectedValue) {
                case 'no':
                    return 'situationAreYouLookingToBuy';
                case 'yes':
                    return 'situationAreYouLookingToBuy';
                default:
                    return 'done';
            }
        },
        formatAnswer: ({ label }) => label
    },
    situationAreYouAPropertyInvestor: {
        label: 'Are you a property investor?',
        options: yesNoNotSayOptions,
        getNext: () => 'done',
        formatAnswer: ({ label }) => label
    },
    situationAreYouLookingToBuy: {
        label: 'Are you looking to buy?',
        options: yesNoNotSayOptions,
        getNext: () => 'done',
        formatAnswer: ({ label }) => label
    },
    situationAreYouAHomeowner: {
        label: 'Are you a homeowner?',
        options: yesNoNotSayOptions,
        getNext: () => 'done',
        formatAnswer: ({ label }) => label
    }
};

export const questionsOrderedMap = Object.keys(questionsMap) as QuestionNames[];
